/**
 * RequirePermission — ด่าน route ที่ตัดสินจาก perm key แทน role flag
 *
 * ใช้เป็น layout route ไม่ต้องใส่ prop ให้ทีละหน้า:
 *
 *   <Route path='' element={<RequirePermission />}>
 *     <Route path='/admin/rbac' element={<RbacHubScreen />} />
 *   </Route>
 *
 * ตัว perm ที่ต้องใช้ถูกดูจาก path ปัจจุบันใน config/routePermissions.js
 * เขียนที่เดียว ไม่ต้องไล่ใส่ prop 97 บรรทัดใน index.js แล้วเสี่ยงใส่ผิดหน้า
 *
 * ── โหมดเริ่มต้นคือ "เตือน ไม่บล็อก" ──────────────────────────────────────
 * ด่านเดิมหลวมกว่าที่ควรมาก (StaffRoute ปล่อยผ่านแค่ "มีแผนกอะไรก็ได้")
 * ถ้าสลับมาบังคับ perm จริงวันแรก คนที่แผนกยังติ๊กสิทธิ์ให้ไม่ครบจะเจอ 403
 * ทันทีทั้งที่เมื่อวานยังทำงานได้ — เป็นความเสียหายที่เกิดกับคนทั้งบริษัท
 * พร้อมกันและมองไม่เห็นล่วงหน้า
 *
 * โหมดนี้จึงปล่อยผ่านเหมือนเดิม แต่ส่ง log ว่า "ถ้าบังคับจริง คนนี้จะโดน
 * บล็อกที่หน้านี้เพราะขาด key ตัวนี้" ไปเก็บที่เซิร์ฟเวอร์ รันสัก 2-3 วัน
 * แล้วเอา log มาเติมสิทธิ์ให้แผนกจนครบ ค่อยเปิดบังคับ
 *
 * เปิดบังคับด้วยการตั้ง REACT_APP_RBAC_ENFORCE=true ตอน build
 * (ไม่ต้องแก้โค้ด และย้อนกลับด้วยการ build ใหม่ได้ทันที)
 */

import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import ForbiddenScreen from "../screens/ForbiddenScreen";
import { permsForPath } from "../config/routePermissions";
import { isAdminUser } from "../utils/authz";
import { reportAccessDenial } from "../utils/accessAudit";

// บล็อกจริงไหม — เดิมตั้งตอน build (REACT_APP_RBAC_ENFORCE) ซึ่งเว็บจริงไม่เคยตั้ง = จดบันทึกอย่างเดียวมาตลอด
// ตอนนี้แอดมินเปิด/ปิดได้เองที่ /admin/org แท็บสิทธิ์ (migration 129 · 20 ต.ค. 69) → userInfo.rbacEnforce
const BUILD_ENFORCING = process.env.REACT_APP_RBAC_ENFORCE === "true";

const RequirePermission = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const { pathname } = useLocation();

  const entry = permsForPath(pathname);
  const required = entry?.perms || null;

  const isAdmin = isAdminUser(userInfo);
  const granted = userInfo?.permissions || [];
  const ENFORCING = BUILD_ENFORCING || userInfo?.rbacEnforce === true;

  // ไม่มี key ที่ต้องใช้ = หน้าที่ตั้งใจเปิดให้ทุกคนที่ล็อกอิน
  // ส่วน entry === null แปลว่า route นี้ยังไม่ได้ลงทะเบียนในตาราง — ปล่อยผ่าน
  // ไว้ก่อนแล้วเตือนไว้ใน log ไม่ใช่บล็อก เพราะหน้าที่เพิ่งเพิ่มเข้ามาไม่ควร
  // ใช้ไม่ได้ทั้งหน้าเพียงเพราะยังไม่มีใครมาเติมตาราง
  // ⚠ ตอนบล็อกจริง หน้าที่ไม่ได้ลงทะเบียน = แอดมินเท่านั้น (เดิมปล่อยทุกคน — ทำหน้าใหม่แล้วลืมลงทะเบียนกลายเป็นเปิดสาธารณะ)
  //   ตอนส่งขึ้นเว็บ scripts/check-route-registry.js ไม่ยอมให้มีหน้าที่ไม่ได้ลงทะเบียนอยู่แล้ว
  //   entry === null = ไม่ได้ลงทะเบียน · perms === null = ลงทะเบียนแล้ว "ล็อกอินแล้วเข้าได้ทุกคน"
  const allowed =
    isAdmin ||
    (entry === null ? !ENFORCING : required === null || required.some((key) => granted.includes(key)));

  // ผูกกับ pattern (string) ไม่ใช่ entry (object ที่สร้างใหม่ทุก render)
  // ไม่งั้น effect จะยิงซ้ำทุกครั้งที่ component re-render โดยไม่จำเป็น
  const pattern = entry?.pattern || null;
  const hasUser = !!userInfo;

  useEffect(() => {
    if (!hasUser) return;

    if (pattern === null) {
      console.warn(
        `[rbac] route ยังไม่ได้ลงทะเบียนใน routePermissions.js: ${pathname}`,
      );
      return;
    }

    if (!allowed) {
      reportAccessDenial({
        path: pathname,
        pattern,
        required,
        enforced: ENFORCING,
      });
    }
  }, [hasUser, pathname, pattern, allowed, required, ENFORCING]);

  // ยังไม่ล็อกอิน → ไป login (ข้อนี้บังคับเสมอ ไม่เกี่ยวกับโหมด audit
  // เพราะเป็นพฤติกรรมเดิมของด่านทุกตัวอยู่แล้ว ไม่ใช่กฎใหม่ที่กำลังทดลอง)
  if (!userInfo) return <Navigate to="/login" replace />;

  if (!allowed && ENFORCING) {
    return <ForbiddenScreen required={(required || []).join(" หรือ ")} />;
  }

  return <Outlet />;
};

export default RequirePermission;
